import Typography from '@mui/material/Typography';
import { DateCell } from '@openx/components-administration/lib/UsersList/DateCell.tsx';
import { ValuePlaceholder } from '@openx/components-core/lib/Value/ValuePlaceholder/ValuePlaceholder.tsx';
import type {
  OrganizationAllowedProvider,
  OrganizationApiKeyData,
  OrganizationPixelMetricData,
} from '@openx/types/organization.ts';
import type { Columns } from '@openx/types/tableHelpers.ts';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getOrganizationsLabels, organizationFields } from '../../../constants.ts';
import { ExpirationDateCell } from '../components/ExpirationDateCell.tsx';

export const useGetTableColumns = () => {
  const { t } = useTranslation();
  const organizationLabels = getOrganizationsLabels(t);

  const renderAllowedExportTypeLabel = useCallback((isSelected: boolean) => (isSelected ? t('Yes') : t('No')), [t]);

  const apiKeysTableColumns: Columns<OrganizationApiKeyData> = useMemo(
    () => [
      {
        title: t('Name'),
        key: 'first_name',
        width: '60%',
        style: {
          paddingTop: '13px',
          paddingBottom: '13px',
        },
        render: ({ first_name }) => (
          <Typography variant="body1Bold" component="span">
            {first_name}
          </Typography>
        ),
      },
      {
        title: organizationLabels[organizationFields.EXPIRATION_DATE],
        key: organizationFields.EXPIRATION_DATE,
        width: '20%',
        render: ({ expires_at }) => <ExpirationDateCell expirationDate={expires_at} />,
      },
      {
        title: organizationLabels[organizationFields.CREATED_DATE],
        key: organizationFields.CREATED_DATE,
        width: '20%',
        render: ({ created_date }) => <DateCell date={created_date} />,
      },
    ],
    [organizationLabels, t]
  );

  const pixelMetricsTableColumns: Columns<OrganizationPixelMetricData> = useMemo(
    () => [
      {
        title: t('Event'),
        key: 'event_type_label',
        width: '40%',
        render: ({ event_type_label }) => (
          <Typography variant="body1Bold" component="span">
            {event_type_label}
          </Typography>
        ),
      },
      {
        title: t('Fire Count (30d)'),
        key: 'fires_30d',
        width: '20%',
        render: ({ fires_30d }) =>
          fires_30d != null ? <Typography>{String(fires_30d)}</Typography> : <ValuePlaceholder />,
      },
      {
        title: t('Unique Count (30d)'),
        key: 'unique_30d',
        width: '20%',
        render: ({ unique_30d }) =>
          unique_30d != null ? <Typography>{String(unique_30d)}</Typography> : <ValuePlaceholder />,
      },
      {
        title: t('Last Fire Date'),
        key: 'last_fire_at',
        width: '20%',
        render: ({ last_fire_at }) => <DateCell date={last_fire_at} />,
      },
    ],
    [t]
  );

  const linkedProvidersTableColumns: Columns<OrganizationAllowedProvider> = useMemo(
    () => [
      {
        title: organizationLabels[organizationFields.NAME],
        key: 'name',
        width: '50%',
        render: ({ name }) => (
          <Typography variant="body1Bold" component="span">
            {name}
          </Typography>
        ),
        disabled: true,
      },
      {
        title: organizationLabels[organizationFields.OA_MATCH],
        key: organizationFields.OA_MATCH,
        width: '15%',
        render: ({ allowed_export_types }) =>
          renderAllowedExportTypeLabel(allowed_export_types.includes(organizationFields.OA_MATCH)),
      },
      {
        title: organizationLabels[organizationFields.BIDSTREAM],
        key: organizationFields.BIDSTREAM,
        width: '15%',
        render: ({ allowed_export_types }) =>
          renderAllowedExportTypeLabel(allowed_export_types.includes(organizationFields.BIDSTREAM)),
      },
      {
        title: organizationLabels[organizationFields.BIDSTREAM_PLUS],
        key: organizationFields.BIDSTREAM_PLUS,
        width: '20%',
        render: ({ allowed_export_types }) =>
          renderAllowedExportTypeLabel(allowed_export_types.includes(organizationFields.BIDSTREAM_PLUS)),
      },
    ],
    [organizationLabels, renderAllowedExportTypeLabel]
  );

  return { apiKeysTableColumns, linkedProvidersTableColumns, pixelMetricsTableColumns };
};
